<template>
  <div
    class="procedure-report-page"
    @click="onClickAway">
    <span
      class="procedure-report-page__title">
      <template v-if="titleParts.prefix">
        <span class="procedure-report-page__title-prefix">{{ titleParts.prefix }}</span>
        <span class="procedure-report-page__title-separator"> / </span>
      </template>
      <span class="procedure-report-page__title-suffix">{{ titleParts.suffix }}</span>
    </span>

    <v-tabs
      background-color="transparent"
      color="primary"
      v-model="tab">
      <v-tab key="procedures" href="#procedures">
        {{ $t('procedureReport.tabs.procedures') }}
      </v-tab>

      <v-tab key="reports" href="#reports">
        {{ $t('procedureReport.tabs.reports') }}
      </v-tab>
    </v-tabs>

    <v-tabs-items
      class="transparent fill-width overflow-visible"
      touchless
      v-model="tab">
      <v-tab-item
        key="procedures"
        value="procedures"
        class="transparent">
        <div class="d-flex flex-row mt-5">
          <ProcedureReportFilter ref="ProcedureReportFilter" />
        </div>
        <div class="my-5 d-flex justify-space-between align-center procedure-report-toolbar">
          <ProcedureReportSummaryCards
            :cards="summaryCards"
            :loading="loadingClassification"
            :active-card="activeSummaryCard"
            @card-click="onCardClick"
          />
          <div class="d-flex flex-row gap-h-15 align-center procedure-report-toolbar__actions">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <Button
                  outlined
                  class="procedure-report-action-button procedure-report-action-button--icon"
                  v-bind="attrs"
                  v-on="on"
                  :loading="loadingExport"
                  :disabled="!hasFiltersSelected"
                  @click="exportReport">
                  <SvgIcon
                    src="icons/map-export.svg"
                    width="22"
                    height="22"
                    style="max-height: 22px; max-width: 22px;"
                    contain
                  />
                </Button>
              </template>
              <span>{{ $t('procedureReport.actions.export') }}</span>
            </v-tooltip>

            <v-menu
              v-model="massActionMenuOpen"
              offset-y
              content-class="procedure-report-mass-action-menu">
              <template v-slot:activator="{ on, attrs }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                    <Button
                      outlined
                      class="procedure-report-action-button procedure-report-action-button--icon"
                      v-bind="{ ...attrs, ...tooltipAttrs }"
                      v-on="{ ...on, ...tooltipOn }"
                      :disabled="!hasMinimalProceduresSelected">
                      <SvgIcon
                        src="icons/map-cog.svg"
                        width="22"
                        height="22"
                        contain
                      />
                    </Button>
                  </template>
                  <span>{{ $t('procedureReport.actions.massActions.label') }}</span>
                </v-tooltip>
              </template>
              <v-list dense>
                <v-list-item
                  v-for="(item, index) in massActionItems"
                  :key="index"
                  class="procedure-report-mass-action-item procedure-report-mass-action-item--menu"
                  :class="{ 'procedure-report-mass-action-item--disabled': item.disabled }"
                  :disabled="item.disabled"
                  @click="!item.disabled && makeAction(item.action)"
                >
                  <v-list-item-icon class="d-flex justify-center mx-3">
                    <v-icon size="18">
                      {{ item.icon }}
                    </v-icon>
                  </v-list-item-icon>

                  <v-list-item-title>
                    {{ item.title }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <Button
                  outlined
                  class="procedure-report-action-button procedure-report-action-button--icon"
                  v-bind="attrs"
                  v-on="on"
                  :disabled="!hasFiltersSelected"
                  @click.stop="addNewProcedure">
                  <v-icon size="20">
                    mdi-plus-circle-outline
                  </v-icon>
                </Button>
              </template>
              <span>{{ $t('procedureReport.actions.addNewRegister') }}</span>
            </v-tooltip>
          </div>
        </div>

        <div class="d-flex flex-column">
          <ProcedureReportTable ref="ProcedureReportTable" />
        </div>
      </v-tab-item>

      <v-tab-item
        key="reports"
        value="reports"
        class="transparent">
        <div class="mt-2">
          <MyReports
            report-type="REPORT_PROCEDURE"
            :reportLayoutName="$t('procedureReport.reports.layoutName')"
            :fetch-reports="fetchProcedureReports"
            :hide-financial-group-filter="true"
            :tab="tab"
            is-admission-map-report
          />
        </div>
      </v-tab-item>
    </v-tabs-items>

    <ReportExportedModal
      ref="ReportExportedModal"
      @goToReport="goToReport"
    />

    <HistoryModal
      ref="HistoryModal"
      :history="recordHistories"
      :identity-columns="historyIdentityColumns"
      @closed="resetHistories"
    />

    <ProcedureReportFormModal
      ref="ProcedureFormModal"
      :title="$t('procedureReport.modals.bulkTitle')"
      :loading="loadingUpdate"
      @save="saveBulkValues"
    />

    <ProcedureReportJustificationModal
      :loading="loadingCorrectRule"
    />

    <SnackbarCustomize ref="ProcedureReportSnackbar" />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';

import ProcedureReportFormModal from '@/components/ProcedureReport/ProcedureReportFormModal.vue';
import ProcedureReportJustificationModal from '@/components/ProcedureReport/ProcedureReportJustificationModal.vue';
import ProcedureReportService from '@/services-http/servicos/ProcedureReportService';
import ReportExportedModal from '@/components/InvoiceParameters/modals/ReportExportedModal.vue';
import ProcedureReportFilter from '@/components/ProcedureReport/ProcedureReportFilter.vue';
import ProcedureReportSummaryCards from '@/components/ProcedureReport/ProcedureReportSummaryCards.vue';
import ProcedureReportTable from '@/components/ProcedureReport/ProcedureReportTable.vue';
import HistoryModal from '@/components/Modals/HistoryModal/HistoryModal.vue';
import Button from '@/components/Default/Button/Button.vue';
import SvgIcon from '@/components/Default/SvgIcon.vue';
import SnackbarCustomize from '@/components/CustomAlerts/SnackbarCustomize.vue';
import {
  getStatusConfigByCardKey,
  isPendingApprovalProcedure,
  procedureReportStatusConfig,
} from '@/store/modules/procedure-report/procedure-report-config';
import MyReports from '../myReports/MyReports.vue';

export default {
  name: 'ProcedureReport',
  components: {
    ProcedureReportFormModal,
    ProcedureReportJustificationModal,
    ProcedureReportFilter,
    ProcedureReportSummaryCards,
    ProcedureReportTable,
    ReportExportedModal,
    HistoryModal,
    SnackbarCustomize,
    MyReports,
    Button,
    SvgIcon,
  },
  data: () => ({
    tab: 'procedures',
    massActionMenuOpen: false,
  }),
  computed: {
    ...mapGetters({
      procedureReportsSelecteds: 'procedure-report/procedureReportsSelecteds',
      recordHistories: 'procedure-report/recordHistories',
      historyModal: 'procedure-report/showHistoryModal',
      snackbar: 'procedure-report/showSnackbarCustomize',
      loadingExport: 'procedure-report/loadingExport',
      loadingUpdate: 'procedure-report/loadingUpdate',
      loadingApproval: 'procedure-report/loadingApproval',
      loadingCorrectRule: 'procedure-report/loadingCorrectRule',
      loadingClassification: 'procedure-report/loadingClassification',
      classification: 'procedure-report/classification',
      hasFiltersSelected: 'procedure-report/hasFiltersSelected',
      filter: 'procedure-report/filter',
    }),
    hasMinimalProceduresSelected() {
      return Array.isArray(this.procedureReportsSelecteds) && this.procedureReportsSelecteds.length >= 2;
    },
    hasApprovableProceduresSelected() {
      return Array.isArray(this.procedureReportsSelecteds)
        && this.procedureReportsSelecteds.some(isPendingApprovalProcedure);
    },
    massActionItems() {
      return [
        {
          action: 'edit',
          icon: 'mdi-pencil-outline',
          title: this.$t('procedureReport.actions.massActions.editValues'),
          disabled: !this.hasMinimalProceduresSelected,
        },
        {
          action: 'approve',
          icon: 'mdi-check-circle-outline',
          title: this.$t('procedureReport.actions.massActions.approve'),
          disabled: !this.hasApprovableProceduresSelected,
        },
      ];
    },
    summaryCards() {
      return procedureReportStatusConfig.map((config) => ({
        key: config.cardKey,
        value: this.classification[config.classificationKey].counter,
        percentage: `${this.formatClassificationPercent(
          this.classification[config.classificationKey].percent,
        )}%`,
        label: this.$t(config.dashboardLabelKey),
        color: config.color,
        icon: config.icon,
      }));
    },
    activeSummaryCard() {
      const match = procedureReportStatusConfig.find((config) => this.filter.status.includes(config.id));
      return match?.cardKey ?? null;
    },
    titleParts() {
      const fullTitle = String(this.$t('procedureReport.title') || '').trim();
      const separator = ' / ';
      const separatorIndex = fullTitle.indexOf(separator);

      if (separatorIndex === -1) {
        return { prefix: '', suffix: fullTitle };
      }

      return {
        prefix: fullTitle.slice(0, separatorIndex),
        suffix: fullTitle.slice(separatorIndex + separator.length),
      };
    },
    historyIdentityColumns() {
      return [
        {
          text: this.$t('historyModal.headers.carrierName'),
          value: 'carrierName',
          sortable: false,
          width: 110,
          class: 'col-history-carrier',
          cellClass: 'col-history-carrier',
          minWidth: 110,
        },
        {
          text: this.$t('procedureReport.fields.procedureCode'),
          value: 'procedureCode',
          sortable: false,
          width: 140,
          class: 'col-history-contract',
          cellClass: 'col-history-contract',
          minWidth: 140,
        },
      ];
    },
  },
  watch: {
    procedureReportsSelecteds() {
      if (!this.hasMinimalProceduresSelected) {
        this.massActionMenuOpen = false;
      }
    },
    historyModal: {
      handler(historyModal) {
        if (historyModal && historyModal.show) {
          this.$refs.HistoryModal.open();
          return;
        }

        this.$refs.HistoryModal.close();
      },
      deep: true,
    },
    snackbar: {
      handler(snackbar) {
        if (snackbar && snackbar.show) {
          const { type, message, timeout } = snackbar;
          this.$refs.ProcedureReportSnackbar.open(type, message, timeout);
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapMutations({
      onClickAway: 'procedure-report/onClickAway',
      resetRecordHistories: 'procedure-report/resetRecordHistories',
      showSnackbar: 'procedure-report/showSnackbar',
    }),
    ...mapActions({
      exportProcedures: 'procedure-report/exportProcedures',
      bulkUpdateProcedures: 'procedure-report/bulkUpdateProcedures',
      applyFilter: 'procedure-report/applyFilter',
      searchByFilter: 'procedure-report/searchByFilter',
      initPage: 'procedure-report/initPage',
      approveProcedures: 'procedure-report/approveProcedures',
    }),
    formatClassificationPercent(percent) {
      const value = Number(percent);
      if (Number.isNaN(value)) return '0';
      return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    },
    async exportReport() {
      if (!this.hasFiltersSelected) return;

      const exported = await this.exportProcedures();
      if (exported) {
        setTimeout(() => {
          this.$refs.ReportExportedModal.open();
        }, 1000);
      }
    },
    async onCardClick(key) {
      const config = getStatusConfigByCardKey(key);
      if (!config) return;

      if (this.activeSummaryCard === key) {
        this.applyFilter({ status: [] });
      } else {
        this.applyFilter({ status: [config.id] });
      }
      await this.searchByFilter({ skipClassification: true });
    },
    fetchProcedureReports(params) {
      const service = new ProcedureReportService();
      const reportParams = {};
      ['receivingDateStart', 'receivingDateEnd', 'status'].forEach((key) => {
        const value = params.get(key);
        if (value) reportParams[key] = value;
      });
      return service.fetchReports(reportParams);
    },
    goToReport() {
      this.tab = 'reports';
    },
    resetHistories() {
      this.resetRecordHistories();
    },
    async saveBulkValues(form) {
      const result = await this.bulkUpdateProcedures(form);
      if ([200, 201, 204].includes(result)) {
        this.$refs.ProcedureFormModal.close();
      }
    },
    makeAction(actionType) {
      switch (actionType) {
        case 'edit':
          if (!this.hasMinimalProceduresSelected) return;
          this.$refs.ProcedureFormModal.open();
          break;
        case 'approve':
          if (!this.hasApprovableProceduresSelected) return;
          this.approveSelectedProcedures();
          break;
        default:
          break;
      }
    },
    async approveSelectedProcedures() {
      this.massActionMenuOpen = false;

      const approvableItems = this.procedureReportsSelecteds.filter(isPendingApprovalProcedure);
      const ids = approvableItems.map((item) => item.id);

      if (!ids.length) {
        this.showSnackbar({
          type: 'warning',
          message: String(this.$t('procedureReport.messages.approveSuccess', {
            approved: 0,
            ignored: this.procedureReportsSelecteds.length,
          })),
          show: true,
        });
        return;
      }

      await this.approveProcedures(ids);
    },
    addNewProcedure() {
      if (!this.hasFiltersSelected) return;
      const table = this.$refs.ProcedureReportTable;
      if (table && typeof table.addNewProcedure === 'function') {
        table.addNewProcedure();
      }
    },
  },
  async created() {
    await this.initPage();
  },
};
</script>

<style lang="scss">
.procedure-report-page {
  min-height: calc(100vh - 280px);
  margin: 20px 40px 20px 20px;
  overflow: visible;

  &__title {
    display: inline-block;
    font-size: 24px;
    line-height: 1.2;
  }

  &__title-prefix {
    color: var(--v-muted-accent-base);
    font-weight: 400;
  }

  &__title-separator {
    color: var(--v-muted-accent-base);
    font-weight: 400;
  }

  &__title-suffix {
    color: var(--v-secondary-base);
    font-weight: 700;
  }

  .procedure-report-toolbar {
    gap: 16px;
    flex-wrap: wrap;
  }

  .procedure-report-action-button {
    --procedure-report-action-color: var(--v-primary-base);
    --procedure-report-action-disabled-color: color-mix(in srgb, var(--procedure-report-action-color) 26%, transparent);

    border-color: var(--procedure-report-action-color) !important;
    color: var(--procedure-report-action-color) !important;
    height: 42px !important;
    max-height: 42px !important;
    min-width: max-content !important;
    padding: 0 16px !important;

    &--icon {
      min-width: 42px !important;
      padding: 0 !important;
      width: 42px !important;
    }

    .v-btn__content,
    .v-btn__content * {
      color: inherit !important;
    }

    .inline-svg-img {
      color: inherit;
      flex: 0 0 auto;
      --svg-color-1: var(--procedure-report-action-color) !important;
      --svg-color-2: var(--procedure-report-action-color) !important;
      --svg-color-3: var(--procedure-report-action-color) !important;
    }

    &.v-btn--disabled {
      border-color: var(--procedure-report-action-disabled-color) !important;
      color: var(--procedure-report-action-disabled-color) !important;
      opacity: 0.45;

      .v-btn__content,
      .v-btn__content * {
        color: inherit !important;
      }

      .inline-svg-img {
        --svg-color-1: var(--procedure-report-action-disabled-color) !important;
        --svg-color-2: var(--procedure-report-action-disabled-color) !important;
        --svg-color-3: var(--procedure-report-action-disabled-color) !important;
      }
    }
  }
}

.theme--dark .procedure-report-page {
  .procedure-report-page__title-suffix {
    color: var(--v-foreground-base);
  }

  .procedure-report-action-button {
    --procedure-report-action-color: var(--v-foreground-base);
  }
}

.procedure-report-mass-action-menu {
  --procedure-report-mass-action-color: var(--v-primary-base);

  .v-list {
    padding: 4px 0;
  }

  .procedure-report-mass-action-item {
    color: var(--procedure-report-mass-action-color) !important;
    min-height: 38px;
    padding: 0 12px;

    .v-icon,
    .v-list-item__title {
      color: inherit !important;
    }

    &--disabled,
    &.v-list-item--disabled {
      --procedure-report-mass-action-color: color-mix(in srgb, var(--v-primary-base) 35%, transparent);
      cursor: default;
      opacity: 1;
      pointer-events: none;

      .v-list-item__icon,
      .v-list-item__icon .v-icon {
        color: var(--procedure-report-mass-action-color) !important;
      }
    }

    .v-list-item__icon {
      margin-bottom: 8px;
      margin-top: 8px;
      min-width: 18px;
    }

    .v-list-item__title {
      font-size: 13px;
    }
  }
}

.theme--dark.procedure-report-mass-action-menu {
  --procedure-report-mass-action-color: var(--v-foreground-base);

  .procedure-report-mass-action-item--disabled,
  .procedure-report-mass-action-item.v-list-item--disabled {
    --procedure-report-mass-action-color: color-mix(in srgb, var(--v-foreground-base) 35%, transparent);

    .v-list-item__icon,
    .v-list-item__icon .v-icon {
      color: var(--procedure-report-mass-action-color) !important;
    }
  }
}
</style>
